import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { SessionActions } from '../../account/sessionSlice';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import type { AllowPublicContentBy } from '../../graphql/generated/graphql';
import PricingModalButton from '../../organization/PricingModalButton';
import SettingCard from '../../settings/SettingCard';
import { isFeatureEnabled } from '../../utils/features';
import * as styles from './PublicContentSettings.module.scss';

const SETTING_FRAGMENT = gql(`
  fragment PublicContentSettings_settings on OrganizationSettings {
    allowPublicContentBy
  }
`);

const UPDATE_PUBLIC_CONTENT_SETTINGS_MUTATION = gql(/** @complexity 4 */ `
  mutation updatePublicContentSettings($allowPublicContentBy: AllowPublicContentBy!) {
    organization: updateOrganization(settings: { allowPublicContentBy: $allowPublicContentBy }) {
      id
      settings {
        ...PublicContentSettings_settings
      }
    }
  }
`);

const options = {
  ONLY_ADMINS: 'Only admins can make posts and topics public',
  ADMIN_TOPICS_USER_POSTS:
    'Only admins can make topics public, anyone can make posts public',
  ALL: 'Anyone can make posts and topics public',
} as const;

type Values = {
  allowPublicContentBy: AllowPublicContentBy | null;
};

interface PublicContentSettingsProps {
  settings: DocumentType<typeof SETTING_FRAGMENT>;
}

const PublicContentSettings = ({ settings }: PublicContentSettingsProps) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [updatePublicContentSettings] = useMutation(
    UPDATE_PUBLIC_CONTENT_SETTINGS_MUTATION,
  );

  const allowPublicContentBy = settings.allowPublicContentBy ?? null;

  const handleSubmit = async (values: Values) => {
    if (values.allowPublicContentBy) {
      const response = await updatePublicContentSettings({
        variables: {
          allowPublicContentBy: values.allowPublicContentBy,
        },
      });

      if (response.data) {
        dispatch(
          SessionActions.setAllowPublicContentBy(
            response.data.organization.settings.allowPublicContentBy ?? null,
          ),
        );
      }

      setIsEditing(false);
    }
  };

  const isPublicPostsTopicsEnabled = isFeatureEnabled('public_posts_topics');
  const description = isPublicPostsTopicsEnabled
    ? !!allowPublicContentBy && options[allowPublicContentBy]
    : 'Public content is not available on your plan';

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Public topics & posts"
          description={
            <Form<Values>
              initialValues={{ allowPublicContentBy }}
              onSubmit={handleSubmit}
            >
              <div className={styles.formFields}>
                {(
                  ['ALL', 'ADMIN_TOPICS_USER_POSTS', 'ONLY_ADMINS'] as const
                ).map((value) => (
                  <Field
                    key={value}
                    label={options[value]}
                    name="allowPublicContentBy"
                    type="radio"
                    value={value}
                  />
                ))}
              </div>
              <div className={styles.description}>
                Regardless of selection, users must have edit access to the
                topic or post to make it public.
              </div>
              <div className={styles.formFooter}>
                <Submit variant="primary">Save</Submit>
              </div>
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            isPublicPostsTopicsEnabled ? (
              <Button onClick={() => setIsEditing(true)} variant="secondary">
                Edit
              </Button>
            ) : (
              <PricingModalButton feature="advancedTopics">
                Upgrade
              </PricingModalButton>
            )
          }
          title="Public topics & posts"
          description={description}
        />
      )}
    </>
  );
};

export default PublicContentSettings;
